import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { ControllerInputSelect, InputTextCleaner } from '../../../../../forms';
import { ControllerInputDateSelect } from '../../../../../forms/react-hook-form';
import styles from '../DocumentationRequired.module.css';
import { MandatoryDocumentProps, REGEXP_DNI, REGEXP_DOCS } from '../DocumentationRequired.model';
import { FormDataApis, SelectOptions } from '../../../ApisForm.model';
import { ApisFormContext, getModelInfant, minMaxDateExpiryDate } from '../../../ApisForm.utils';
import { DocMandatoryFields } from '../../../ApisForm.constants';

export const MandatoryDocument = ({ isInfant = false }: MandatoryDocumentProps) => {
	const { t } = useTranslation(['documentation-required', 'common']);
	const {
		register,
		formState: { isSubmitted, isSubmitting },
		watch,
		clearErrors
	} = useFormContext<FormDataApis>();
	const { listOptions, isFormDisabled = false } = useContext(ApisFormContext);
	const modelInfant = getModelInfant(isInfant);

	const { minDate, maxDate } = minMaxDateExpiryDate();
	const expiryDate = watch(DocMandatoryFields.EXPIRY_DATE);
	const expiryDateInfant = watch(`${modelInfant}${DocMandatoryFields.EXPIRY_DATE}`);

	useEffect(() => {
		clearErrors();
	}, [expiryDate, expiryDateInfant, clearErrors]);

	return (
		<div className={styles.box_content}>
			<div className={styles.box_input}>
				<ControllerInputSelect
					name={`${modelInfant}${DocMandatoryFields.ISSUER_COUNTRY}`}
					label={t('DOCUMENT_COUNTRY')}
					options={listOptions.countries as SelectOptions[]}
					rules={{ required: t('DOCUMENT_COUNTRY_REQUIRED_ERROR') }}
					disabled={isFormDisabled}
				/>
			</div>

			{/* Select document type */}
			<div className={styles.box_input}>
				<ControllerInputSelect
					name={`${modelInfant}${DocMandatoryFields.TYPE}`}
					label={t('DOCUMENT_TYPE')}
					options={listOptions.mandatoryDocsTypes as SelectOptions[]}
					rules={{ required: t('DOCUMENT_TYPE_REQUIRED_ERROR') }}
					triggersName={`${modelInfant}${DocMandatoryFields.NUMBER}`}
					data-testid="mandatory-document-type"
					disabled={isFormDisabled}
				/>
			</div>
			{/* Input document number */}
			<div className={styles.box_input}>
				<InputTextCleaner
					{...register(`${modelInfant}${DocMandatoryFields.NUMBER}`, {
						required: t('GENERAL_INPUT_REQUIRED_ERROR'),
						validate: {
							type: (newValue, { documentationRequired }) => {
								const selectedType = documentationRequired.mandatory.type as SelectOptions;
								const checkSubmit = (isSubmitting || isSubmitted) && newValue;

								return checkSubmit && selectedType.value === 'DNI' && !REGEXP_DNI.test(newValue)
									? t('DNI_FORMAT_ERROR')
									: undefined;
							}
						}
					})}
					name={`${modelInfant}${DocMandatoryFields.NUMBER}`}
					label={t('DOCUMENT_NUMBER')}
					regexCleaner={REGEXP_DOCS}
					maxLength={15}
					className={styles.box_input_text}
					labelStyles={styles.labelStyles}
					data-testid="mandatory-document-number"
					disabled={isFormDisabled}
				/>
			</div>
			{/* Select expiry date */}
			<div className={styles.box_input}>
				<ControllerInputDateSelect
					name={`${modelInfant}${DocMandatoryFields.EXPIRY_DATE}`}
					label={t('EXPIRY_DATE')}
					rules={{ required: t('GENERAL_INPUT_REQUIRED_ERROR') }}
					minDate={minDate}
					maxDate={maxDate}
					disabled={isFormDisabled}
				/>
			</div>
		</div>
	);
};
